/**
 * useEventOptions Hook
 *
 * Fetches and filters event options (e.g., "Dynamo Invite", "Rockets Games")
 * from the EPG API for specific months and categories.
 *
 * Created: 2026-01-21
 */

import { useState, useCallback, useMemo } from 'react';
import { epgApi } from '@shared/services/epgApi';
import type { EventOption } from '@shared/types/epg';
import type { MonthCode } from './useEpgContactFilter';

/**
 * Event option with normalized label for filtering
 */
export interface FilteredEventOption extends EventOption {
  /** Whether this option belongs to the Sporting category */
  isSporting: boolean;
  /** Whether this option belongs to the Concerts category */
  isConcerts: boolean;
  /** Option IDs for each month (same label has different IDs per month in Pipedrive) */
  monthIds: Record<string, number>;
}

/**
 * Sporting event keywords - matches backend logic in epg_service.py
 */
const SPORTING_KEYWORDS = [
  'rockets',
  'astros',
  'texans',
  'dynamo',
  'dash',
  'cougars',
  'rice',
  'baseball',
  'basketball',
  'football',
  'soccer',
  'game',
  'match',
];

/**
 * Concert/music event keywords
 */
const CONCERT_KEYWORDS = ['concert', 'music', 'comedy', 'show', 'performance', 'theater'];

/**
 * Check if an option label indicates a sporting event
 */
function isSportingEvent(label: string): boolean {
  const lower = label.toLowerCase();

  // Check for section header
  if (lower.includes('-----sports-----') || lower.includes('-----sport-----')) {
    return false; // Don't include header as selectable
  }

  return SPORTING_KEYWORDS.some((kw) => lower.includes(kw));
}

/**
 * Check if an option label indicates a concert/music event
 */
function isConcertEvent(label: string): boolean {
  const lower = label.toLowerCase();

  // Check for section header
  if (lower.includes('-----')) {
    return false; // Don't include header as selectable
  }

  return CONCERT_KEYWORDS.some((kw) => lower.includes(kw));
}

export interface UseEventOptionsResult {
  /** All available event options for selected months */
  eventOptions: FilteredEventOption[];
  /** Event options filtered by category (Sporting/Concerts) */
  filteredOptions: FilteredEventOption[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Fetch event options for the given months */
  loadEventOptions: (months: MonthCode[]) => Promise<void>;
  /** Filter options by event category */
  filterByCategory: (categories: string[]) => FilteredEventOption[];
  /** Get all month-specific IDs for a given event option ID (for API calls) */
  getEventOptionIds: (optionId: number) => number[];
  /** Get option by ID */
  getOptionById: (optionId: number) => FilteredEventOption | undefined;
  /** Clear loaded options */
  clearOptions: () => void;
}

/**
 * Hook for fetching and filtering EPG event options
 */
export function useEventOptions(): UseEventOptionsResult {
  const [eventOptions, setEventOptions] = useState<FilteredEventOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  /**
   * Load event options for selected months
   */
  const loadEventOptions = useCallback(async (months: MonthCode[]) => {
    if (months.length === 0) {
      setEventOptions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`[Event Options] Fetching options for months: ${months.join(', ')}`);

      // Fetch event options from API - returns options organized by month
      const response = await epgApi.getEventOptions('Events');

      // Collect unique options across selected months, preserving month-specific IDs
      const optionsMap = new Map<string, FilteredEventOption>();

      for (const month of months) {
        const monthOptions = response.months[month] || [];

        for (const option of monthOptions) {
          const label = option.label || (option as unknown as Record<string, unknown>).name as string || '';

          // Skip section headers (e.g., "-----SPORTS-----")
          if (label.includes('-----')) {
            continue;
          }

          const labelKey = label.toLowerCase();

          if (optionsMap.has(labelKey)) {
            // Add this month's ID to existing option
            const existing = optionsMap.get(labelKey)!;
            existing.monthIds[month] = option.id;
          } else {
            // Create new option with this month's ID
            const isSporting = isSportingEvent(label);
            const isConcerts = isConcertEvent(label);

            optionsMap.set(labelKey, {
              ...option,
              label,
              isSporting,
              isConcerts,
              monthIds: { [month]: option.id },
            });
          }
        }
      }

      const allOptions = Array.from(optionsMap.values()).sort((a, b) =>
        (a.label || '').localeCompare(b.label || '')
      );

      console.log(`[Event Options] Loaded ${allOptions.length} unique options across ${months.length} months`);
      setEventOptions(allOptions);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load event options';
      console.error('[Event Options] Error:', message);
      setError(message);
      setEventOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Filter options by event category
   */
  const filterByCategory = useCallback(
    (categories: string[]): FilteredEventOption[] => {
      setSelectedCategories(categories);

      if (categories.length === 0) {
        return eventOptions;
      }

      return eventOptions.filter((option) => {
        if (categories.includes('Sporting') && option.isSporting) return true;
        if (categories.includes('Concerts') && option.isConcerts) return true;
        return false;
      });
    },
    [eventOptions]
  );

  /**
   * Filtered options based on selected categories
   */
  const filteredOptions = useMemo(() => {
    if (selectedCategories.length === 0) {
      return eventOptions;
    }

    return eventOptions.filter((option) => {
      if (selectedCategories.includes('Sporting') && option.isSporting) return true;
      if (selectedCategories.includes('Concerts') && option.isConcerts) return true;
      return false;
    });
  }, [eventOptions, selectedCategories]);

  /**
   * Get all month-specific IDs for a given event option ID
   * Used when filtering - the API needs all month-specific IDs for the selected events
   */
  const getEventOptionIds = useCallback(
    (optionId: number): number[] => {
      // Find the option by any of its month IDs
      for (const option of eventOptions) {
        if (option.id === optionId || Object.values(option.monthIds).includes(optionId)) {
          return Object.values(option.monthIds);
        }
      }
      return [];
    },
    [eventOptions]
  );

  /**
   * Get option by ID (finds by any month ID)
   */
  const getOptionById = useCallback(
    (optionId: number): FilteredEventOption | undefined => {
      return eventOptions.find(
        (opt) => opt.id === optionId || Object.values(opt.monthIds).includes(optionId)
      );
    },
    [eventOptions]
  );

  /**
   * Clear loaded options
   */
  const clearOptions = useCallback(() => {
    setEventOptions([]);
    setError(null);
    setSelectedCategories([]);
  }, []);

  return {
    eventOptions,
    filteredOptions,
    isLoading,
    error,
    loadEventOptions,
    filterByCategory,
    getEventOptionIds,
    getOptionById,
    clearOptions,
  };
}

export default useEventOptions;
